import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Bot, LineChart, Workflow, Sparkles, ArrowLeft, ImageOff, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import api, { resolveMedia } from '../lib/api';
import { Dialog, DialogContent } from '../components/ui/dialog';

const iconMap = { Bot, LineChart, Workflow, Sparkles };
const statusStyles = {
  Available: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  Beta: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  'Coming soon': 'bg-white/5 text-slate-300 border-white/15'
};

const AIProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null); // {url}

  useEffect(() => {
    api.get('/products')
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="bg-[#05080f] min-h-screen">
      <Navbar />

      <section className="relative pt-36 pb-16 md:pt-44 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.15),transparent_60%)]"></div>
        <div className="perspective-grid"></div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">AI Products</div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-white max-w-4xl">
            Purpose-built <span className="gradient-text">AI products</span>
            <br /> for modern teams
          </h1>
          <p className="mt-6 text-slate-400 max-w-2xl text-base md:text-lg">
            Explore our growing suite of AI products designed to help you automate workflows,
            unlock insights, and build intelligent experiences on top of your existing infrastructure.
          </p>
        </div>
      </section>

      <section className="relative py-10 md:py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-slate-400 py-16 text-center">Loading products...</div>
          ) : products.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => {
                const Icon = iconMap[p.icon] || Sparkles;
                return (
                  <div key={p.id} className="group card-dark rounded-3xl overflow-hidden flex flex-col hover:border-amber-400/30 transition-colors">
                    <div className="aspect-[16/9] bg-white/[0.02] border-b border-white/5 overflow-hidden flex items-center justify-center">
                      {p.thumbnail ? (
                        <img src={resolveMedia(p.thumbnail)} alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      ) : (
                        <ImageOff size={22} className="text-slate-600" />
                      )}
                    </div>

                    <div className="p-7 flex flex-col flex-1 relative">
                      <div className="flex items-start justify-between mb-5">
                        <div className="h-11 w-11 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                          <Icon className="text-amber-400" size={20} strokeWidth={1.6} />
                        </div>
                        <span className={`text-[11px] px-2.5 py-1 rounded-full border ${statusStyles[p.status] || statusStyles['Coming soon']}`}>
                          {p.status}
                        </span>
                      </div>

                      <h3 className="text-white text-xl font-semibold">{p.name}</h3>
                      {p.tagline && <div className="text-amber-400 text-sm mt-1">{p.tagline}</div>}
                      <p className="text-slate-400 text-sm leading-relaxed mt-3 flex-1">{p.description}</p>

                      {p.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-5">
                          {p.tags.map((t) => (
                            <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 text-slate-300 border border-white/10">{t}</span>
                          ))}
                        </div>
                      )}

                      {p.screenshots?.length > 0 && (
                        <div className="flex gap-2 mt-5 overflow-x-auto pb-1">
                          {p.screenshots.slice(0, 4).map((s) => (
                            <button key={s} onClick={() => setViewer({ url: resolveMedia(s) })}
                              className="shrink-0 h-16 w-24 rounded-lg overflow-hidden border border-white/10 hover:border-amber-400/40 transition-colors">
                              <img src={resolveMedia(s)} alt="screenshot" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}

                      <div className="mt-6 pt-5 border-t border-white/5">
                        <a href="/#contact" className="btn-gradient inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-white text-sm font-medium">
                          Contact for pricing <ArrowUpRight size={14} />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(56,189,248,0.12),transparent_60%)]"></div>
            <div className="relative">
              <h2 className="font-display text-2xl md:text-4xl font-semibold text-white tracking-tight mb-4">
                Have a product idea in mind?
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto mb-8">
                We collaborate with founders and enterprises to co-build AI products from concept
                to production. Tell us what you are working on.
              </p>
              <a href="/#contact">
                <Button className="btn-gradient rounded-full h-11 px-8 text-white border-0">
                  Get in touch
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-4xl bg-[#0b1220] border-white/10 p-2">
          {viewer && <img src={viewer.url} alt="preview" className="w-full h-auto rounded-lg" />}
        </DialogContent>
      </Dialog>

      <Footer />
    </main>
  );
};

const EmptyState = () => (
  <div className="card-dark rounded-3xl p-12 text-center">
    <div className="h-14 w-14 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5">
      <Sparkles className="text-amber-400" size={24} />
    </div>
    <h3 className="text-white text-2xl font-semibold mb-3">Products coming soon</h3>
    <p className="text-slate-400 max-w-lg mx-auto">
      We are actively building AI products that will be published here. Check back soon or reach
      out to be notified when we launch.
    </p>
  </div>
);

export default AIProductsPage;
